import { useCallback, useEffect, useMemo, useState } from 'react';
import { type MessageDescriptor, type PrimitiveType } from 'react-intl';
import {
  deleteInvitationTextFromLocalStorage,
  getInvitationFooterText,
  getInvitationFormattedText,
  getInvitationTextDefaultValue,
  getInvitationTextFromLocalStorage,
  setInvitationTextToLocalStorage,
} from './invitationTextHelper';
import { type UseFormTrigger, type UseFormResetField } from 'react-hook-form';
import {
  type DateInputValue,
  type TimeInputValue,
} from '@doxyme/ui/components/foundations';
import { type InviteViaTextMessageFormValues } from '../InviteViaTextMessageModalContent/types';
import type { InvitationFieldValue } from '../types';

export type UseInvitationTextBoxProps = {
  dateFieldValue: DateInputValue;
  timeFieldValue: TimeInputValue;
  invitationFieldValue: InvitationFieldValue;
  passcode: string | null;
  roomUrl: string;
  providerName: string;
  locale: string;
  resetField: UseFormResetField<InviteViaTextMessageFormValues>;

  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string;
  trigger: UseFormTrigger<InviteViaTextMessageFormValues>;

  /**
   * The string separator between the invitation text and the footer text
   */
  inputToFooterSeparator?: string;
};

type UseInvitationTextBoxReturnProps = {
  // The formatted footer text we show below the invitation text area. It's a combination of date / time / room / passcode.
  invitationFooterText: string;
  // Whether or not the provider has amended the default value for the text area. Note: if we use a value from local storage - then that's treated as custom text too.
  isUsingDefaultTextContent: boolean;
  // The complete invitation text which is a combination of the invitation text area value & the footer text.
  invitationFormattedText: string;
  // The passcode text which is shown in the footer. Required for calendar ICS file generation.
  onRestoreDefaultMessage: () => void;
};

/**
 * This hook is responsible for keeping track of the invitation text. A few things happen here.
 * 1 - We setup the default text to be used for the invitation field. This is based on whichever value is in local storage or the default value for the currently selected locale / language
 * 2 - We keep track of which invitation text is in use i.e. default vs custom. This is required as part of the submission routine.
 * 3 - We setup the invitation field footer text which consists of the room url / date + time (optional) / passcode (optional).
 * 4 - We return the full invitation text, which is submitted to the API. This is a combination of #1 and #3 above.
 */
export const useInvitationTextBox = ({
  dateFieldValue,
  timeFieldValue,
  invitationFieldValue,
  passcode,
  roomUrl,
  providerName,
  locale,
  resetField,
  formatMessage,
  trigger,
  inputToFooterSeparator,
}: UseInvitationTextBoxProps): UseInvitationTextBoxReturnProps => {
  const [invitationTextDefaultValue, setInvitationTextDefaultValue] =
    useState('');

  const invitationFooterText = useMemo(
    () =>
      getInvitationFooterText(
        dateFieldValue,
        timeFieldValue,
        roomUrl,
        locale,
        passcode,
        formatMessage,
      ),
    [dateFieldValue, timeFieldValue, passcode, formatMessage, locale],
  );

  /*
   If there's a local storage value for the given locale - let's use it to set the input value and trigger revalidation.
   Otherwise - we fallback to the default for the selected language (locale).
   It's worth commenting here that `formatMessage` function is what changes when the locale has fully loaded, hence adding it to the dependency array.
  */
  useEffect(() => {
    const defaultValue = getInvitationTextDefaultValue(
      providerName,
      formatMessage,
    );

    setInvitationTextDefaultValue(defaultValue);

    const localStorageValue = getInvitationTextFromLocalStorage(locale);

    resetField('invitationField', {
      defaultValue: localStorageValue ?? defaultValue,
      keepDirty: false,
      keepTouched: false,
      keepError: false,
    });

    void trigger('invitationField');
  }, [providerName, formatMessage]);

  // Persist the text to local storage in real time, but only if it's not the default value.
  useEffect(() => {
    if (invitationTextDefaultValue === '') {
      return;
    }

    if (invitationFieldValue !== invitationTextDefaultValue) {
      setInvitationTextToLocalStorage(locale, invitationFieldValue as string);
    } else {
      deleteInvitationTextFromLocalStorage(locale);
    }
  }, [invitationFieldValue]);

  const onRestoreDefaultMessageCallback = useCallback(() => {
    resetField('invitationField', {
      defaultValue: invitationTextDefaultValue,
      keepDirty: false,
      keepTouched: false,
      keepError: false,
    });
  }, [invitationTextDefaultValue]);

  return {
    invitationFooterText,
    isUsingDefaultTextContent:
      invitationFieldValue === invitationTextDefaultValue,
    onRestoreDefaultMessage: onRestoreDefaultMessageCallback,
    invitationFormattedText: getInvitationFormattedText(
      invitationFieldValue,
      invitationFooterText,
      inputToFooterSeparator,
    ),
  };
};
